import React, { useMemo } from 'react';
import { Table, TableProps, Pagination, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { PaginatedData } from '../../types/pagination.types';
import SearchBar from './SearchBar';
import Filters from './Filters';
import { useTranslation } from 'react-i18next';
import { FilterItem, SelectedFilter } from '../../types/filters.types';

/**
 * Responsive PaginatedTable component with built-in responsive features and Bootstrap integration
 *
 * Features:
 * - Horizontal scrolling for tables with many columns
 * - Responsive column visibility using the 'responsive' prop
 * - Bootstrap responsive classes for column hiding/showing
 * - Custom scroll configuration via 'scrollX' prop
 * - Bootstrap table-responsive wrapper for better mobile experience
 * - Bootstrap breakpoint-aware responsive behavior
 *
 * Example usage:
 * ```tsx
 * const columns: ColumnConfig<User>[] = [
 *   {
 *     title: 'Name',
 *     key: 'name',
 *     dataIndex: 'name',
 *     responsive: ['lg', 'md', 'sm'], // Ant Design responsive
 *     hideOnBreakpoint: ['xs'], // Bootstrap responsive classes
 *     minWidth: 120
 *   },
 *   {
 *     title: 'Email',
 *     key: 'email',
 *     dataIndex: 'email',
 *     responsive: ['lg', 'md'], // Hide on sm and xs screens
 *     hideOnBreakpoint: ['sm', 'xs'], // Bootstrap responsive classes
 *     minWidth: 200
 *   },
 *   {
 *     title: 'Phone',
 *     key: 'phone',
 *     dataIndex: 'phone',
 *     className: 'text-center', // Custom Bootstrap classes
 *     hideOnBreakpoint: ['md', 'sm', 'xs'], // Only show on lg and xl
 *     minWidth: 150
 *   }
 * ];
 *
 * <PaginatedTable
 *   columns={columns}
 *   data={users}
 *   scrollX={1200} // Custom scroll width
 *   // ... other props
 * />
 * ```
 *
 * Bootstrap Breakpoints:
 * - xs: < 576px
 * - sm: ≥ 576px
 * - md: ≥ 768px
 * - lg: ≥ 992px
 * - xl: ≥ 1200px
 */

export interface ColumnConfig<T> {
  title: string;
  key: string;
  dataIndex?: string | string[];
  render?: (value: any, record: T, index: number) => React.ReactNode;
  sorter?: boolean | ((a: T, b: T) => number);
  filters?: { text: string; value: string | number | boolean }[];
  onFilter?: (value: any, record: T) => boolean;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  fixed?: 'left' | 'right' | boolean;
  ellipsis?: boolean;
  responsive?: ('lg' | 'md' | 'sm' | 'xs')[]; // Responsive breakpoints for column visibility
  minWidth?: number; // Minimum width for responsive behavior (Ant Design only accepts numbers)
  className?: string; // Custom CSS classes for the column
  hideOnBreakpoint?: ('xs' | 'sm' | 'md' | 'lg' | 'xl')[]; // Bootstrap responsive hide classes
}

export interface PaginatedTableProps<T> {
  data?: T[];
  columns: ColumnConfig<T>[];
  loading?: boolean;
  pagination?: PaginatedData<T>;
  rowKey?: string | ((record: T) => string);
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearchChange?: (value: string) => void;
  showSearchBar?: boolean;
  showFilters?: boolean;
  availableFilters?: FilterItem[];
  onFilterChange?: (filters: SelectedFilter[]) => void;
  className?: string;
  showSizeChanger?: boolean;
  tableProps?: Omit<TableProps<T>, 'columns' | 'dataSource' | 'loading' | 'pagination' | 'rowKey'>;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  scrollX?: string | number | true; // Custom horizontal scroll configuration
}

function PaginatedTable<T extends Record<string, any>>({
  data = [],
  columns,
  loading = false,
  pagination,
  rowKey = 'id',
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  showSearchBar = false,
  showFilters = false,
  availableFilters,
  onFilterChange,
  className,
  showSizeChanger = false,
  tableProps,
  sortField,
  sortOrder,
  scrollX = 'max-content',
}: PaginatedTableProps<T>) {
  const { t } = useTranslation();

  // Convert column config to Ant Design's column format
  const tableColumns = useMemo<ColumnsType<T>>(() => {
    return columns.map((column) => {
      const { key, responsive, minWidth, className, hideOnBreakpoint, ...rest } = column;

      // If this column is sortable and matches the current sortField,
      // apply the appropriate sortOrder
      let columnSortOrder: 'ascend' | 'descend' | null = null;
      if (column.sorter && sortField === key) {
        columnSortOrder = sortOrder === 'asc' ? 'ascend' : 'descend';
      }

      // Build responsive classes for Bootstrap
      let responsiveClasses = className || '';
      if (hideOnBreakpoint) {
        hideOnBreakpoint.forEach((breakpoint) => {
          responsiveClasses += ` hide-${breakpoint}`;
        });
      }

      return {
        key,
        ...rest,
        sortOrder: columnSortOrder,
        // Apply responsive properties if provided
        ...(responsive && { responsive }),
        ...(minWidth && { minWidth }),
        // Apply custom classes including Bootstrap responsive classes
        ...(responsiveClasses && { className: responsiveClasses.trim() }),
      };
    });
  }, [columns, sortField, sortOrder]);

  // Set up pagination config for Ant Design Table
  const paginationConfig = useMemo(() => {
    if (!pagination) return false;

    return {
      current: pagination.page,
      pageSize: pagination.limit,
      total: pagination.totalDocs,
      showSizeChanger,
      onChange: onPageChange,
      onShowSizeChange: (_: number, size: number) => {
        onPageSizeChange?.(size);
      },
      showTotal: (total: number, range: [number, number]) =>
        `${range[0]}-${range[1]} of ${total} items`,
    };
  }, [pagination, onPageChange, onPageSizeChange, showSizeChanger]);

  return (
    <div className={className}>
      <Spin spinning={loading} tip={t('common.loading')}>
        <div className="row mb-md-3">
          {showSearchBar && onSearchChange && (
            <div className={'col-md-4 col-12'}>
              <SearchBar onSearchChange={onSearchChange} disabled={loading} />
            </div>
          )}

          {showFilters && availableFilters && availableFilters.length > 0 && (
            <div className={'col-md-8 col-12'}>
              <Filters
                availableFilters={availableFilters}
                onFilterChange={onFilterChange!}
                disabled={loading}
              />
            </div>
          )}
        </div>

        <div className="row">
          <div className="col-12">
            <div className="table-responsive table-responsive-sm table-responsive-md table-responsive-lg table-responsive-xl">
              <Table
                columns={tableColumns}
                dataSource={data}
                loading={false} // We're handling loading with Spin component
                pagination={false}
                rowKey={rowKey}
                showSorterTooltip={{ title: 'Click to sort' }}
                sortDirections={['ascend', 'descend']} // Only allow these two directions, no cancel
                scroll={{ x: scrollX }} // Enable horizontal scrolling for many columns
                {...tableProps}
                bordered
                className="table-responsive-bootstrap"
              />
            </div>
          </div>

          {pagination && (
            <div className="mt-4 flex justify-end">
              <Pagination {...paginationConfig} disabled={loading} />
            </div>
          )}
        </div>
      </Spin>
    </div>
  );
}

export default PaginatedTable;
